import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'News', path: '/news' },
];

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !trigger;

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={isTransparent ? 0 : 2}
        sx={{ 
          background: isTransparent 
            ? 'linear-gradient(180deg, rgba(0,51,102,0.8) 0%, transparent 100%)'
            : '#003366',
          backdropFilter: isTransparent ? 'none' : 'blur(10px)',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ py: 1 }}>
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textDecoration: 'none',
                flexGrow: 1,
              }}
            >
              <Box
                component="img"
                src="/thr-logo-small.png"
                alt=""
                sx={{ 
                  height: 40, 
                  width: 'auto',
                  display: { xs: 'none', sm: 'block' },
                }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  color: '#fff',
                  letterSpacing: '-0.01em',
                }}
              >
                TEXAS HIGHWAY RANGERS
              </Typography>
            </Box>

            {isMobile ? (
              <IconButton
                edge="end"
                onClick={() => setMobileOpen(true)}
                sx={{ color: '#fff' }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    sx={{
                      color: '#fff',
                      fontWeight: location.pathname === item.path ? 700 : 500,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 4,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: location.pathname === item.path ? '60%' : 0,
                        height: 2,
                        bgcolor: '#ffc107',
                        transition: 'width 0.3s',
                      },
                      '&:hover': {
                        background: 'rgba(255,255,255,0.1)',
                        '&::after': {
                          width: '60%',
                        },
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                <Button
                  href="https://www.paypal.com/donate/?hosted_button_id=DGZ6BNXSWDCAC&return=https://www.highwayrangers.com/donated&cancel_return=https://www.highwayrangers.com/canceldonation"
                  target="_blank"
                  variant="contained"
                  sx={{
                    ml: 2,
                    bgcolor: '#ffc107',
                    color: '#003366',
                    fontWeight: 700,
                    '&:hover': {
                      bgcolor: '#ffca28',
                    },
                  }}
                >
                  Donate
                </Button>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: { 
            width: '100%', 
            maxWidth: 320,
            bgcolor: '#003366',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={{ px: 2 }}>
          {navItems.map((item) => (
            <ListItem
              key={item.path}
              component={Link}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              sx={{
                borderRadius: 2,
                mb: 1,
                bgcolor: location.pathname === item.path ? 'rgba(255,193,7,0.2)' : 'transparent',
                color: '#fff',
                borderLeft: location.pathname === item.path ? '3px solid #ffc107' : '3px solid transparent',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItem>
          ))}
          <ListItem sx={{ mt: 2 }}>
            <Button
              href="https://www.paypal.com/donate/?hosted_button_id=DGZ6BNXSWDCAC&return=https://www.highwayrangers.com/donated&cancel_return=https://www.highwayrangers.com/canceldonation"
              target="_blank"
              variant="contained"
              fullWidth
              sx={{
                bgcolor: '#ffc107',
                color: '#003366',
                fontWeight: 700,
                py: 1.5,
                '&:hover': {
                  bgcolor: '#ffca28',
                },
              }}
            >
              Donate
            </Button>
          </ListItem>
        </List>
      </Drawer>

      {/* Spacer for fixed navbar (only on non-home pages) */}
      {!isHome && <Toolbar sx={{ mb: 2 }} />}
    </>
  );
}

export default Navbar;
